import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Row, InputNumber } from 'antd';
import { ProductsAPI } from 'src/modules/products/api/ProductsAPI';
import { SectionTitle } from 'src/components/forms/SectionTitle';
import { ImageGallery } from 'src/modules/products/components/ImageGallery';
import { SelectWithPagination } from 'src/components/SelectWithPagination';
import { useAttributes } from 'src/hooks/useProvideAttributes';
import { useTranslation } from 'react-i18next';
import { textFrom } from 'src/utils/textFrom';
import { DeletionModal } from 'src/components/DeletionModal';
import { useAuth } from 'src/modules/auth/provider/auth.provider';
import { SCOPE } from 'src/utils/scope-utils';

const NO_SIZE = 'no-size';

const attributeToOption = item => ({ value: item.key, label: item.label });

const asDefaultOptions = (value, label) => (value ? [{ value, label: label ?? value }] : []);

export const VariantDetails = ({
  language,
  variant,
  productId,
  onEditVariant,
  closeDrawer,
  selectedVariantAvailabilities,
  onGalleryOrderUpdate,
  onGalleryUpdate,
  variantGalleryFiles,
  isUpdatePending
}) => {
  const { t } = useTranslation();
  const text = textFrom('components.variantDetails', t);
  const { selectedUserScope, userDepartments } = useAuth();
  const { LOCALBUSINESS } = SCOPE;
  const [isDeletionModalVisible, setIsDeletionModalVisible] = useState(false);

  const [form] = Form.useForm();
  const { getAttrsForInfiniteSelect } = useAttributes();
  const [isNoSizeSelected, setIsNoSizeSelected] = useState(false);

  const [colorOptions, setColorOptions] = useState([]);
  const [colorPagination, setColorPagination] = useState({});
  const [sizeScaleOptions, setSizeScaleOptions] = useState([]);
  const [sizeScalePagination, setSizeScalePagination] = useState({});
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sizePagination, setSizePagination] = useState({});

  useEffect(() => {
    if (variant) {
      const fieldsValue = {
        color: variant.attributeVariants.color.key,
        sizeScale: variant.attributeVariants.sizeScale.businessKey,
        size: variant.attributeVariants.size.businessKey,
        manufacturerCode: variant.manufacturerCode,
        price: variant.prices ? variant.prices.sellingPrice : null,
        availabilities: selectedVariantAvailabilities.reduce((acc, variant) => {
          acc[variant.id] = variant.availability;
          return acc;
        }, {})
      };
      if (fieldsValue.sizeScale === NO_SIZE) {
        setIsNoSizeSelected(true);
      }
      form.setFieldsValue(fieldsValue);
    }
  }, [form, variant, selectedVariantAvailabilities]);

  const onChangeSizeScale = useCallback((value) => {
    if (value === NO_SIZE && !isNoSizeSelected) {
      setIsNoSizeSelected(true);
    } else if (value !== NO_SIZE && isNoSizeSelected) {
      setIsNoSizeSelected(false);
    }
  }, [isNoSizeSelected]);

  const deleteVariant = async () => {
    await ProductsAPI.deleteVariant(productId, variant.id);
    window.location.reload(false);
  };

  const showDeletionModal = () => {
    setIsDeletionModalVisible(true);
  };

  const hideDeletionModal = () => {
    setIsDeletionModalVisible(false);
  };

  const loadInfiniteSelect = useCallback((page, key) => {
    const department = selectedUserScope === LOCALBUSINESS ? userDepartments[0] : undefined;
    const sortingKey = 'localeName';
    const sortingOrder = 'asc';
    return getAttrsForInfiniteSelect(key, page, language, department, sortingKey, sortingOrder);
  }, [getAttrsForInfiniteSelect]);

  const getAttributeOptionsAtPage = useCallback(async (key, page, setOptions, setPagination) => {
    const res = await loadInfiniteSelect(page, key);
    setOptions(res?.data?.data?.items || []);
    setPagination({ ...res?.data?.data });
  }, [loadInfiniteSelect]);

  const getColorAtPage = useCallback((page) => (
    getAttributeOptionsAtPage('color', page, setColorOptions, setColorPagination)
  ), [getAttributeOptionsAtPage]);

  const getSizeScaleAtPage = useCallback((page) => (
    getAttributeOptionsAtPage('sizeScale', page, setSizeScaleOptions, setSizeScalePagination)
  ), [getAttributeOptionsAtPage]);

  const getSizeAtPage = useCallback((page) => (
    getAttributeOptionsAtPage('size', page, setSizeOptions, setSizePagination)
  ), [getAttributeOptionsAtPage]);

  return (
    <>
      <div>
        <Form
          onFinish={onEditVariant}
          form={form}
          layout='vertical'
          autoComplete='off'
          initialValues={{
            color: null,
            sizeScale: null,
            size: null,
            manufacturerCode: null,
            price: null,
            availability: []
          }}
          scrollToFirstError
        >
          <div className='relative pb-16'>
            {/* Manufacture Code */}
            <Row className='px-4 py-4'>
              <Form.Item
                className='w-full'
                name='manufacturerCode'
                label={text('EAN')}
                rules={[
                  {
                    required: true,
                    message: text('EANRequired')
                  }
                ]}
              >
                <Input placeholder={text('EAN')} />
              </Form.Item>
            </Row>

            {/* Color */}
            <Row className='px-4 pb-4'>
              <Form.Item
                className='w-full'
                name='color'
                label={text('color')}
                rules={[{ required: true, message: text('colorRequired') }]}
              >
                <SelectWithPagination
                  searchable={false}
                  items={colorOptions}
                  getItemsAtPage={getColorAtPage}
                  pagination={colorPagination}
                  toOption={attributeToOption}
                  defaultOptions={asDefaultOptions(variant.attributeVariants.color.key, variant.attributeVariants.color.localeName)}
                />
              </Form.Item>
            </Row>

            {/* Scale */}
            <Row className='px-4 pb-4'>
              <Form.Item
                className='w-full'
                name='sizeScale'
                label={text('scale')}
                rules={[
                  { required: true, message: text('scaleRequired') }
                ]}
              >
                <SelectWithPagination
                  searchable={false}
                  items={sizeScaleOptions}
                  getItemsAtPage={getSizeScaleAtPage}
                  pagination={sizeScalePagination}
                  toOption={attributeToOption}
                  defaultOptions={asDefaultOptions(variant.attributeVariants.sizeScale.businessKey, variant.attributeVariants.sizeScale.businessKey)}
                  onChange={onChangeSizeScale}
                />
              </Form.Item>
            </Row>

            {/* Size */}
            <Row className='px-4 pb-4'>
              <Form.Item
                name='size'
                label={text('size')}
                className='w-full'
                rules={[{ required: !isNoSizeSelected, message: text('sizeRequired') }]}
                disabled={isNoSizeSelected}
              >
                <SelectWithPagination
                  searchable={false}
                  disabled={isNoSizeSelected}
                  items={sizeOptions}
                  getItemsAtPage={getSizeAtPage}
                  pagination={sizePagination}
                  toOption={attributeToOption}
                  defaultOptions={asDefaultOptions(variant.attributeVariants.size.businessKey, variant.attributeVariants.size.businessKey)}
                />
              </Form.Item>
            </Row>

            <hr className='mt-2' />

            <Row className='px-4 pt-4'>
              <SectionTitle title={text('prices')} />
            </Row>

            {/* Prices */}
            <Row className='px-4 pb-4'>
              <Form.Item
                name='price'
                label={text('price')}
                className='w-full px-4 pb-4'
                rules={[
                  () => ({
                    validator (_, value) {
                      if (value === null || value === undefined || value === '') {
                        return Promise.reject(new Error(text('priceRequired')));
                      } else if (Number(value) < 0) {
                        return Promise.reject(new Error(text('priceInvalid')));
                      } else return Promise.resolve();
                    }
                  })]}
              >
                <Input
                  min='0'
                  step='0.1'
                  addonBefore={variant.prices?.currency?.symbol}
                  placeholder={text('pricePlaceholder')}
                  className='w-full'
                />
              </Form.Item>
            </Row>
            <hr className='mt-2' />
            {/* Gallery */}
            <Row className='px-4 pt-4'>
              <SectionTitle title={text('gallery')} />
            </Row>
            <Row className='pl-4 pb-4'>
              <ImageGallery
                fileList={variantGalleryFiles.filter(img => (img?.info?.type === 'cover'))}
                detailFileList={variantGalleryFiles.filter(img => (!img.info || img?.info?.type !== 'cover'))}
                onGalleryUpdate={(file, fileList, type) => onGalleryUpdate(file, fileList, type)}
                onGalleryOrderUpdate={(fileList) => onGalleryOrderUpdate(fileList)}
              />
            </Row>
            {!!selectedVariantAvailabilities.length && <hr className='mt-2' />}
            <Row className='pl-4 pb-4'>
              <SectionTitle title={text('availability')} />
              {selectedVariantAvailabilities.length
                ? selectedVariantAvailabilities.map((availability, index) => (
                  <Form.Item
                    key={'random-prefix' + availability.id}
                    name={['availabilities', availability.id]}
                    label={availability.warehouse?.name}
                    className='w-full px-4 pb-4'
                    rules={[
                      { required: true, message: text('availabilityRequired') }
                    ]}
                  >
                    <InputNumber
                      className='w-full'
                      min='0'
                      placeholder={text('availabilityPlaceholder')}
                      parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                ))
                : text('noAvailabilityFound')}
            </Row>
            <Row className='p-4'>
              {/* Delete Product */}
              <Button
                type='danger'
                htmlType='button'
                className='uppercase'
                onClick={showDeletionModal}
              >
                {text('deleteVariant')}
              </Button>
            </Row>
          </div>
          <div className='absolute bottom-0 z-40 bg-white flex w-full justify-end pr-6 py-4'>
            <Button
              className='mr-2 uppercase'
              type='text'
              onClick={closeDrawer}
            >
              {text('cancel')}
            </Button>
            <Button
              className='uppercase'
              type='primary'
              htmlType='submit'
              loading={isUpdatePending}
              disabled={isUpdatePending}
            >
              {text('saveChanges')}
            </Button>
          </div>
        </Form>
      </div>

      <DeletionModal
        onOk={deleteVariant}
        visible={isDeletionModalVisible}
        onCancel={hideDeletionModal}
        msgOnSuccess={text('onDeleteOk')}
        msgOnError={text('onDeleteErr')}
      >
        <div>{text('askConfirmation')}</div>
      </DeletionModal>
    </>

  );
};
